import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2, Clock, FileSearch, DollarSign, CreditCard, BadgeCheck,
  FileSignature, Stamp, CalendarCheck, Search, AlertCircle, Upload, RefreshCw,
  Receipt, FileText, MapPin, Calendar, ExternalLink, Tag, Building2, Copy,
  Lock, Paperclip, Ban, Check,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDate, fmtDateTime, fmtNum } from "@/lib/format";
import { localizeCountry, localizeVisaType } from "@/lib/locales";
import RiyalSymbol from "@/components/RiyalSymbol";
import LandingBackdrop from "@/components/portal/LandingBackdrop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/portal`;
const APP_NO_RE = /^VTA-(\d{4}-\d{5}|\d{2}-[A-HJ-NP-Z2-9]{6,8})$/i;

/**
 * Simplified stages shown to clients on the horizontal timeline.
 * Each stage groups one or more underlying workflow status codes.
 * Order MUST match real workflow progression.
 */
const STAGE_DEFS = [
  { key: "submitted",  codes: ["submitted", "awaiting_documents"], icon: FileSearch,
    label: { ar: "تم استلام طلبك", en: "Request received" } },
  { key: "review",     codes: ["under_review", "sent_to_pricing"], icon: Clock,
    label: { ar: "مراجعة المستندات", en: "Document review" } },
  { key: "fees",       codes: ["fees_set"], icon: DollarSign,
    label: { ar: "تم تحديد الرسوم", en: "Fees set" } },
  { key: "pick_appt",  codes: ["appointment_selection"], icon: CalendarCheck,
    label: { ar: "اختيار الموعد", en: "Pick appointment" } },
  { key: "pricing",    codes: ["awaiting_payment", "payment_rejected"], icon: CreditCard,
    label: { ar: "السداد", en: "Payment" } },
  { key: "payment",    codes: ["payment_uploaded"], icon: CreditCard,
    label: { ar: "مراجعة مالية", en: "Financial review" } },
  { key: "approved",   codes: ["payment_approved"], icon: BadgeCheck,
    label: { ar: "تم اعتماد السداد", en: "Payment approved" } },
  { key: "processing", codes: ["processing", "in_progress"], icon: FileSignature,
    label: { ar: "قيد التنفيذ", en: "Processing" } },
  { key: "completed",  codes: ["completed", "appointment_booked"], icon: Stamp,
    label: { ar: "اكتملت إجراءات المكتب", en: "Office procedures completed" } },
];

/**
 * Internal-office timeline — shorter 5-step journey shown to clients whose
 * application was registered at the office (walk-in / WhatsApp). The
 * employee already collected payment + booked appointment on the spot, so
 * the management approval gates are absent.
 */
const INTERNAL_STAGE_DEFS = [
  { key: "intake",     codes: ["office_intake"], icon: FileSearch,
    label: { ar: "تم تسجيل المعاملة", en: "Application registered" } },
  { key: "pick_appt",  codes: ["office_appointment_selection"], icon: CalendarCheck,
    label: { ar: "اختيار الموعد", en: "Pick appointment" } },
  { key: "paid",       codes: ["payment_recorded", "receipt_issued"], icon: Receipt,
    label: { ar: "تم تحصيل الرسوم", en: "Payment collected" } },
  // Office-only internal tracking stages (with_official_entity, official_entity_approved)
  // are NOT shown to clients. Our role ends at "اكتملت إجراءات المكتب" — we don't claim
  // any involvement in the embassy's approval or rejection decision.
  { key: "appt_set",   codes: ["with_official_entity", "official_entity_approved", "appointment_uploaded"], icon: CalendarCheck,
    label: { ar: "تم تأكيد موعدك", en: "Your appointment is confirmed" } },
  { key: "completed",  codes: ["office_completed", "completed"], icon: Stamp,
    label: { ar: "اكتملت إجراءات المكتب", en: "Office procedures completed" } },
];

const isInternalApp = (data) => (
  data?.workflow_path === "internal" || data?.is_office_submission === true
);

const stagesFor = (data) => (isInternalApp(data) ? INTERNAL_STAGE_DEFS : STAGE_DEFS);

const stageIndexFromCode = (code, data) => {
  if (!code) return 0;
  const defs = stagesFor(data);
  const i = defs.findIndex((s) => s.codes.includes(code));
  return i >= 0 ? i : 0;
};

export default function PortalTrack() {
  const { theme, dir, lang } = useI18n();
  const navigate = useNavigate();
  const { num: paramNum } = useParams();
  const [num, setNum] = useState(paramNum || "");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const T_ALL = {
    ar: {
        title: "تتبع معاملتك",
        sub: "أدخل رقم المعاملة الذي وصلك بعد تقديم الطلب",
        ph: "VTA-26-XXXXXX",
        btn_search: "تتبع",
        invalid: "رقم المعاملة غير صحيح",
        not_found: "لم نعثر على معاملة بهذا الرقم",
        too_many: "محاولات كثيرة. حاول بعد قليل.",
        rejected_doc: "اعتذرنا — تم رفض المعاملة",
        client_ref: "عزيزي العميل",
        client_named: (name) => `عزيزي ${name}`,
        timeline_title: "خط سير المعاملة",
        notes_title: "ملاحظات عامة",
        no_notes: "لا توجد ملاحظات عامة",
        stage_note_label: "ملاحظة من الفريق",
        reupload_label: "رفع المستند المطلوب",
        reupload_done: "تم الرفع بنجاح",
        country: "الدولة",
        visa: "نوع التأشيرة",
        submitted_at: "تاريخ التقديم",
        last_update: "آخر تحديث",
        invoice_title: "فاتورة المعاملة",
        invoice_hint: "فيما يلي تفصيل الأتعاب — اعتمدت بواسطة الإدارة",
        invoice_total: "الإجمالي",
        invoice_currency: "ريال",
        bank_transfer_title: "بيانات التحويل البنكي",
        upload_receipt: "رفع إيصال السداد",
        receipt_uploaded: "تم رفع الإيصال — بانتظار الاعتماد",
        warning_24h: "ملاحظة: تُلغى المعاملة خلال 24 ساعة في حال عدم السداد.",
        appt_title: "تفاصيل الموعد",
        appt_center: "المركز",
        appt_city: "المدينة",
        appt_date: "التاريخ",
        appt_time: "الساعة",
        appt_ref: "الرقم المرجعي",
        appt_pdf: "تنزيل ملف الموعد (PDF)",
        appt_status_booked: "مؤكد",
        appt_status_rescheduled: "تم تأجيله",
        appt_status_cancelled: "ملغي",
        appt_status_done: "تم",
    },
    en: {
        title: "Track your request",
        sub: "Enter the application number you received after submitting",
        ph: "VTA-26-XXXXXX",
        btn_search: "Track",
        invalid: "Invalid application number",
        not_found: "We couldn't find an application with this number",
        too_many: "Too many attempts. Please wait a bit.",
        rejected_doc: "We're sorry — the request was rejected",
        client_ref: "Dear customer",
        client_named: (name) => `Dear ${name}`,
        timeline_title: "Your request journey",
        notes_title: "General notes",
        no_notes: "No general notes",
        stage_note_label: "Team note",
        reupload_label: "Upload requested document",
        reupload_done: "Uploaded successfully",
        country: "Country",
        visa: "Visa type",
        submitted_at: "Submitted on",
        last_update: "Last update",
        invoice_title: "Invoice",
        invoice_hint: "Detailed pricing breakdown — approved by management",
        invoice_total: "Total",
        invoice_currency: "SAR",
        bank_transfer_title: "Bank transfer details",
        upload_receipt: "Upload payment receipt",
        receipt_uploaded: "Receipt uploaded — awaiting approval",
        warning_24h: "Note: the application is cancelled after 24h if no payment is uploaded.",
        appt_title: "Appointment details",
        appt_center: "Center",
        appt_city: "City",
        appt_date: "Date",
        appt_time: "Time",
        appt_ref: "Reference number",
        appt_pdf: "Download appointment PDF",
        appt_status_booked: "Confirmed",
        appt_status_rescheduled: "Rescheduled",
        appt_status_cancelled: "Cancelled",
        appt_status_done: "Completed",
    },
    ur: {
        title: "اپنی درخواست ٹریک کریں",
        sub: "وہ درخواست نمبر درج کریں جو آپ کو جمع کرانے کے بعد ملا",
        ph: "VTA-26-XXXXXX",
        btn_search: "ٹریک",
        invalid: "غلط درخواست نمبر",
        not_found: "ہمیں اس نمبر سے کوئی درخواست نہیں ملی",
        too_many: "بہت زیادہ کوششیں۔ تھوڑی دیر بعد کوشش کریں۔",
        rejected_doc: "معذرت — درخواست مسترد کر دی گئی",
        client_ref: "محترم گاہک",
        client_named: (name) => `محترم ${name}`,
        timeline_title: "آپ کی درخواست کا سفر",
        notes_title: "عمومی نوٹس",
        no_notes: "کوئی عمومی نوٹس نہیں",
        stage_note_label: "ٹیم کا نوٹ",
        reupload_label: "مطلوبہ دستاویز اپ لوڈ کریں",
        reupload_done: "کامیابی سے اپ لوڈ ہو گیا",
        country: "ملک",
        visa: "ویزا کی قسم",
        submitted_at: "جمع کرانے کی تاریخ",
        last_update: "آخری اپ ڈیٹ",
        invoice_title: "انوائس",
        invoice_hint: "تفصیلی فیس — انتظامیہ سے منظور شدہ",
        invoice_total: "کل",
        invoice_currency: "ریال",
        bank_transfer_title: "بینک ٹرانسفر کی تفصیلات",
        upload_receipt: "ادائیگی کی رسید اپ لوڈ کریں",
        receipt_uploaded: "رسید اپ لوڈ ہو گئی — منظوری کا انتظار",
        warning_24h: "نوٹ: ادائیگی نہ ہونے کی صورت میں درخواست 24 گھنٹے میں منسوخ ہو جاتی ہے۔",
        appt_title: "اپوائنٹمنٹ کی تفصیلات",
        appt_center: "مرکز",
        appt_city: "شہر",
        appt_date: "تاریخ",
        appt_time: "وقت",
        appt_ref: "حوالہ نمبر",
        appt_pdf: "اپوائنٹمنٹ PDF ڈاؤن لوڈ کریں",
        appt_status_booked: "تصدیق شدہ",
        appt_status_rescheduled: "وقت تبدیل",
        appt_status_cancelled: "منسوخ",
        appt_status_done: "مکمل",
    },
  };
  const T = T_ALL[lang] || T_ALL.ar;

  const search = async (n) => {
    const value = (n || num).trim().toUpperCase();
    setErr(""); setData(null);
    if (!APP_NO_RE.test(value)) { setErr(T.invalid); return; }
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/track/${value}`);
      setData(data);
      // Update URL so user can share/bookmark
      navigate(`../track/${value}`, { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 429) setErr(T.too_many);
      else setErr(detail || T.not_found);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (paramNum) search(paramNum);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramNum]);

  const css = makeStyles(theme);

  return (
    <div dir={dir} style={css.page} data-testid="portal-track">
      <LandingBackdrop theme={theme} variant="ambient" />
      <button
        onClick={() => navigate("/portal")}
        style={css.backBtn}
        data-testid="track-back"
      >
        <span style={{ fontSize: 16, lineHeight: 1 }}>{dir === "rtl" ? "→" : "←"}</span>
        <span>{lang === "ar" ? "رجوع للرئيسية" : "Back to home"}</span>
      </button>

      <header style={css.header}>
        <h1 style={css.h1}>{T.title}</h1>
        <p style={css.sub}>{T.sub}</p>
      </header>

      <div style={css.searchRow}>
        <div style={css.inputWrap}>
          <Search size={16} style={css.searchIcon} />
          <input
            value={num} onChange={(e) => setNum(e.target.value.toUpperCase())}
            placeholder={T.ph} style={css.input} dir="ltr"
            onKeyDown={(e) => e.key === "Enter" && search()}
            data-testid="track-input"
          />
        </div>
        <button onClick={() => search()} disabled={busy} style={css.btn} data-testid="track-btn">
          {busy ? "…" : T.btn_search}
        </button>
      </div>

      {err && (
        <div style={css.err} data-testid="track-error">
          <AlertCircle size={18} strokeWidth={2.2} />
          <span>{err}</span>
        </div>
      )}

      {data && <TrackBody data={data} T={T} theme={theme} lang={lang} dir={dir} reload={() => search(data.application_number)} />}
    </div>
  );
}

/* ────────────────── Track Body ────────────────── */
function TrackBody({ data, T, theme, lang, dir, reload }) {
  const css = makeStyles(theme);
  const [tab, setTab] = useState("status");

  // Auto-popup state must live BEFORE any early returns (rules of hooks).
  // It's safe to compute initial state from `data` here; the condition that
  // decides whether to show it is applied at render time.
  // We include BOTH new doc requests AND legacy stage notes that demand a
  // document upload so the popup fires for older applications too.
  const _newDocsForHook = data?.document_requests || [];
  const _legacyDocsForHook = (data?.notes || []).filter((n) => n.requires_document_type);
  const _pendingForHook =
    _newDocsForHook.filter((r) => r.status !== "uploaded").length +
    _legacyDocsForHook.filter((n) => !n.document_uploaded_at).length;
  const _totalDocsForHook = _newDocsForHook.length + _legacyDocsForHook.length;
  const docPopupKey = `vta_doc_popup_${data?.application_number}_${_totalDocsForHook}`;
  const [docPopupOpen, setDocPopupOpen] = useState(() => {
    if (_pendingForHook === 0) return false;
    try { return localStorage.getItem(docPopupKey) !== "seen"; } catch { return true; }
  });

  // When the office STAS-refunds an application it is archived. The
  // portal still renders a friendly closure screen but blocks every
  // interactive action (payment, doc upload, appointment, muaqqib) —
  // everything that returns from `useTrackData()` is hidden.
  if (data?.archived) {
    const archivedAt = data.archived_at ? fmtDate(data.archived_at) : "";
    const refunded = data.archive_reason === "refunded";
    return (
      <section style={{
        marginTop: 24, padding: "32px 20px", borderRadius: 18,
        background: "rgba(220,38,38,0.06)", border: "2px solid #dc2626",
        textAlign: "center",
      }} data-testid="portal-archived-banner">
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 14, color: "#dc2626" }}>
          <Lock size={48} strokeWidth={2.2} />
        </div>
        <h2 style={{ color: "#dc2626", fontSize: 20, fontWeight: 900, margin: "0 0 10px" }}>
          {lang === "en"
            ? "This application is closed"
            : lang === "ur"
              ? "یہ درخواست بند ہو چکی ہے"
              : "تم إغلاق هذه المعاملة"}
        </h2>
        <p style={{ fontSize: 14, lineHeight: 1.8, opacity: 0.85, margin: "0 0 14px", whiteSpace: "pre-line" }}>
          {(() => {
            if (!refunded) {
              return lang === "en"
                ? `Closed${archivedAt ? ` on ${archivedAt}` : ""}.`
                : lang === "ur"
                  ? `بند کر دی گئی${archivedAt ? ` ${archivedAt} کو` : ""}۔`
                  : `أُغلقت${archivedAt ? ` بتاريخ ${archivedAt}` : ""}.`;
            }
            const net = data.invoice_refund_net_to_client;
            const exempt = !!data.invoice_refund_exempt;
            const gross = data.invoice_refund_amount;
            const pct = exempt ? 0 : 10;
            const netTxt = net != null ? fmtNum(net) : "";
            const grossTxt = gross != null ? fmtNum(gross) : "";
            if (lang === "en") {
              const base = `The fees were refunded${archivedAt ? ` on ${archivedAt}` : ""}.`;
              const detail = netTxt
                ? ` Refunded amount: ${netTxt} SAR${exempt
                    ? " (office fees fully waived — 100% returned)"
                    : ` (10% office retention of ${grossTxt} SAR — 90% returned)`}.`
                : "";
              return `${base}${detail}\nNo further actions are available.`;
            }
            if (lang === "ur") {
              const base = `یہ درخواست بند کر دی گئی ہے اور فیس${archivedAt ? ` ${archivedAt} کو` : ""} واپس کر دی گئی۔`;
              const detail = netTxt
                ? ` واپس شدہ رقم: ${netTxt} ريال${exempt
                    ? " (دفتر کی 10% فیس مکمل معاف — 100% واپس)"
                    : " (10% دفتر کی فیس کاٹی گئی — 90% واپس)"}۔`
                : "";
              return `${base}${detail}`;
            }
            const base = `تم إغلاق هذه المعاملة واسترداد المبلغ${archivedAt ? ` بتاريخ ${archivedAt}` : ""}.`;
            const detail = netTxt
              ? ` المبلغ المُسترجَع: ${netTxt} ر.س${exempt
                  ? " (تم إعفاء العميل من الأتعاب الأولية — استُرجع 100%)"
                  : ` (احتُسبت الأتعاب الأولية 10% من ${grossTxt} ر.س — استُرجع 90%)`}.`
              : "";
            return `${base}${detail}\nلا توجد إجراءات متاحة على هذه المعاملة.`;
          })()}
        </p>
        <div style={{ fontSize: 11, opacity: 0.65, fontFamily: "monospace", marginTop: 10 }} dir="ltr">
          {data.application_number}
        </div>
      </section>
    );
  }

  const stageDefs = stagesFor(data);
  const currentIdx = stageIndexFromCode(data.current_status_code, data);
  const country = localizeCountry(data.country, lang);
  const visa = localizeVisaType(data.visa_type, lang);
  const clientName = data.client_full_name || data.members?.[0]?.full_name;
  const greeting = clientName ? T.client_named(clientName) : T.client_ref;
  const isRejected = data.current_status_code === "rejected";

  // Group public notes by stage_code for under-circle display
  const notesByStage = {};
  const generalNotes = [];
  (data.notes || []).forEach((n) => {
    if (n.stage_code) {
      const idx = stageIndexFromCode(n.stage_code, data);
      const key = stageDefs[idx].key;
      (notesByStage[key] = notesByStage[key] || []).push(n);
    } else {
      generalNotes.push(n);
    }
  });

  // ────────────────────────────────────────────────────────────────
  // Mobile-first layout — instead of a long vertical scroll that
  // overwhelms elderly users on phones, we present ONE focused section
  // at a time via a compact horizontal tab bar. Hero stays pinned at
  // the top as the always-visible identity card.
  //   • status   → timeline + appointment-picker + notes
  //   • invoice  → invoice card + receipt uploader
  //   • appointment → confirmed appointment card
  //   • muaqqib  → muaqqib info (only visible in late stages)
  // Tabs that have no content for the current stage are auto-hidden.
  // ────────────────────────────────────────────────────────────────
  const inPaymentStage = (
    data.current_status_code === "fees_set" ||
    data.current_status_code === "awaiting_payment" ||
    data.current_status_code === "payment_rejected"
  );
  const hasInvoice = (data.pricing_items?.length || 0) > 0;
  // Business rule (Iteration 66): the invoice tab is HIDDEN from the
  // client until the office has booked an appointment (the office can
  // also confirm via "appointment_chosen" if the client picked one of
  // the proposed options). Pre-appointment, even if pricing items
  // already exist, the client isn't shown the bill — payment comes
  // strictly AFTER appointment is locked.
  const appointmentLocked = !!(
    data.appointment_chosen ||
    data.appointment ||
    data.current_status_code === "appointment_booked" ||
    data.current_status_code === "awaiting_payment" ||
    data.current_status_code === "payment_uploaded" ||
    data.current_status_code === "payment_approved" ||
    data.current_status_code === "processing" ||
    data.current_status_code === "completed"
  );
  const showInvoiceTab = hasInvoice && appointmentLocked;
  const hasAppointmentCard = !!(data.appointment || data.appointment_attachment)
    && data.appointment?.status !== "cancelled";
  const showMuaqqibTab = (() => {
    // Only show the Muaqqib tab when a muaqqib is ACTUALLY assigned to the
    // application. Empty/placeholder tabs confuse the client.
    const hasAssigned = !!(data.muaqqib_assigned_id
      || data.muaqqib_assigned_name
      || data.muaqqib_profile
      || (data.muaqqib_member_ids && data.muaqqib_member_ids.length > 0));
    if (!hasAssigned) return false;
    const code = data.current_status_code;
    const lateStages = ["appointment_booked", "processing", "in_progress", "completed",
                        "official_entity_approved", "appointment_uploaded",
                        "with_official_entity", "office_completed"];
    const paymentOk = data.payment?.status === "approved"
      || data.is_office_submission === true
      || data.workflow_path === "internal";
    return lateStages.includes(code) && paymentOk;
  })();

  // Office-requested documents — UNIFIED stream that includes:
  //   1) the NEW `document_requests` array (added via the workspace
  //      "Add Doc Request" panel)
  //   2) LEGACY stage notes that carry `requires_document_type` (older
  //      apps created before the new system; they should still surface in
  //      the new tab + popup so clients don't have to hunt for them).
  const newDocRequests = (data.document_requests || []).map((r) => ({
    id: r.id,
    title: r.title,
    status: r.status,
    kind: "request",
  }));
  const legacyDocNotes = (data.notes || [])
    .filter((n) => n.requires_document_type)
    .map((n) => ({
      id: n.id,
      title: `${docTypeLabel(n.requires_document_type)}${n.body ? " — " + n.body : ""}`,
      status: n.document_uploaded_at ? "uploaded" : "pending",
      kind: "note",
    }));
  const docRequests = [...newDocRequests, ...legacyDocNotes];
  const pendingDocs = docRequests.filter((r) => r.status !== "uploaded");
  const showDocsTab = docRequests.length > 0;
  const docsBadge = pendingDocs.length > 0 ? pendingDocs.length : null;
  const closeDocPopup = () => {
    setDocPopupOpen(false);
    try { localStorage.setItem(docPopupKey, "seen"); } catch { /* ignore */ }
  };

  const tabs = [
    { k: "status", ar: "الحالة", en: "Status", ur: "حالت" },
    ...(showDocsTab ? [{ k: "docs", ar: "المستندات", en: "Documents", ur: "دستاویزات", badge: docsBadge }] : []),
    ...(showInvoiceTab ? [{ k: "invoice", ar: "الفاتورة", en: "Invoice", ur: "انوائس" }] : []),
    ...(hasAppointmentCard ? [{ k: "appointment", ar: "الموعد", en: "Appointment", ur: "اپوائنٹمنٹ" }] : []),
    ...(showMuaqqibTab ? [{ k: "muaqqib", ar: "المعقّب", en: "Muaqqib", ur: "ایجنٹ" }] : []),
    ...(generalNotes.length > 0 ? [{ k: "notes", ar: "ملاحظات", en: "Notes", ur: "نوٹس" }] : []),
  ];
  // If the active tab disappears (e.g. invoice closed), fall back to status.
  const activeTab = tabs.find((t) => t.k === tab) ? tab : "status";

  return (
    <>
      {/* Hero card — always visible compact identity */}
      <section style={css.hero} data-testid="track-hero">
        <div style={css.heroGreet}>{greeting}</div>
        <div style={css.heroNumber} dir="ltr">{data.application_number}</div>
        <div style={css.heroMeta}>
          {country && <Meta theme={theme} label={T.country} value={country} />}
          {visa && <Meta theme={theme} label={T.visa} value={visa} />}
          {data.submission_date && <Meta theme={theme} label={T.submitted_at} value={fmtDate(data.submission_date)} />}
          {data.updated_at && <Meta theme={theme} label={T.last_update} value={fmtDate(data.updated_at)} />}
        </div>
      </section>

      {/* Sticky tab bar — premium pill style, scrolls horizontally on tiny screens */}
      {tabs.length > 1 && (
        <nav
          dir={dir}
          style={{
            position: "sticky", top: 0, zIndex: 50,
            display: "flex", gap: 6, padding: "10px 4px",
            margin: "8px -4px 14px",
            overflowX: "auto",
            background: theme.bg,
            borderBottom: `1px solid ${theme.border}`,
            WebkitOverflowScrolling: "touch",
          }}
          data-testid="track-tabbar"
        >
          {tabs.map((t) => {
            const isActive = activeTab === t.k;
            const hasBadge = t.badge != null && t.badge > 0;
            return (
              <button
                key={t.k}
                type="button"
                onClick={() => setTab(t.k)}
                data-testid={`track-tab-${t.k}`}
                style={{
                  flex: "0 0 auto",
                  padding: "8px 16px",
                  borderRadius: 999,
                  border: `1.5px solid ${isActive ? theme.gold : (hasBadge ? "#dc2626" : theme.border)}`,
                  background: isActive ? theme.gold : (hasBadge ? "rgba(220,38,38,0.10)" : "transparent"),
                  color: isActive ? "#1a1a1a" : (hasBadge ? "#dc2626" : theme.text),
                  fontSize: 13, fontWeight: 800,
                  cursor: "pointer", fontFamily: "inherit",
                  transition: "background 140ms, color 140ms, border-color 140ms",
                  whiteSpace: "nowrap",
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                {lang === "en" ? t.en : lang === "ur" ? t.ur : t.ar}
                {hasBadge && (
                  <span style={{
                    background: isActive ? "#1a1a1a" : "#dc2626",
                    color: "#fff",
                    fontSize: 10, fontWeight: 900,
                    padding: "1px 7px", borderRadius: 999,
                    minWidth: 18, textAlign: "center",
                  }}>{t.badge}</span>
                )}
              </button>
            );
          })}
        </nav>
      )}

      {/* ─── STATUS TAB ─── timeline + appointment picker + payment gate hint */}
      {activeTab === "status" && (
        <>
          <section style={css.timelineWrap} data-testid="track-timeline">
            <div style={css.timelineTitle}>{T.timeline_title}</div>
            <HorizontalTimeline
              currentIdx={currentIdx}
              isRejected={isRejected}
              theme={theme} lang={lang} dir={dir}
              notesByStage={notesByStage}
              applicationNumber={data.application_number}
              T={T}
              stageDefs={stageDefs}
            />
          </section>
          <AppointmentPicker applicationNumber={data.application_number} theme={theme} lang={lang} dir={dir} onPicked={reload} />
          {/* Once an appointment is set/uploaded, surface it RIGHT HERE on the
              status tab too (instead of forcing the client to switch tabs).
              Office-set appointment data + downloadable attachment both render. */}
          {hasAppointmentCard && (
            <AppointmentCard
              appointment={data.appointment || {}}
              attachment={data.appointment_attachment}
              applicationNumber={data.application_number}
              theme={theme} lang={lang} dir={dir}
            />
          )}
          {/* CTA "Open invoice" only when the invoice is currently visible
              to the client (appointment locked + items exist). After the
              office has approved the payment, the phrase drops "to pay" so
              the client understands nothing further is owed. */}
          {showInvoiceTab && (() => {
            const isPaid = data.payment?.status === "approved"
              || ["payment_approved", "processing", "completed"].includes(data.current_status_code);
            const label = isPaid
              ? (lang === "ar" ? "افتح الفاتورة" : lang === "ur" ? "انوائس کھولیں" : "Open invoice")
              : (lang === "ar" ? "افتح الفاتورة للسداد" : lang === "ur" ? "انوائس کھولیں" : "Open invoice to pay");
            return (
              <button
                onClick={() => setTab("invoice")}
                data-testid={isPaid ? "track-jump-invoice-paid" : "track-jump-invoice"}
                style={{
                  width: "100%", marginTop: 14, padding: "14px 16px",
                  borderRadius: 14, border: "none",
                  background: theme.gold, color: "#1a1a1a",
                  fontSize: 15, fontWeight: 900, cursor: "pointer",
                  fontFamily: "inherit",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10,
                }}
              >
                <CreditCard size={18} strokeWidth={2.4} />
                <span>{label}</span>
              </button>
            );
          })()}
        </>
      )}

      {/* ─── INVOICE TAB ─── */}
      {activeTab === "invoice" && hasInvoice && (() => {
        const hasOptions = (data.appointment_options || []).length > 0;
        const hasChosen = !!data.appointment_chosen;
        if (hasOptions && !hasChosen) {
          return (
            <section dir={dir} style={{
              background: "rgba(220,38,38,0.06)",
              border: `2px dashed #dc2626`,
              borderRadius: 16, padding: 18, marginBottom: 18,
              textAlign: "center",
            }} data-testid="payment-gate-pick-appointment-first">
              <div style={{ fontSize: 14, fontWeight: 900, color: "#dc2626", marginBottom: 6, display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Ban size={16} strokeWidth={2.4} />
                <span>{lang === "ar" ? "اختر موعدك أولاً" : lang === "ur" ? "پہلے اپوائنٹمنٹ منتخب کریں" : "Pick your appointment first"}</span>
              </div>
              <button
                onClick={() => setTab("status")}
                style={{ marginTop: 10, padding: "8px 14px", borderRadius: 10,
                  background: theme.gold, color: "#1a1a1a", border: "none",
                  fontWeight: 800, cursor: "pointer", fontSize: 12 }}
              >
                {lang === "ar" ? "اذهب لاختيار الموعد" : "Go pick appointment"}
              </button>
            </section>
          );
        }
        return isInternalApp(data)
          ? <InternalPaymentCard data={data} theme={theme} lang={lang} />
          : <InvoiceCard data={data} theme={theme} lang={lang} dir={dir} onUploaded={reload} />;
      })()}

      {/* ─── APPOINTMENT TAB ─── */}
      {activeTab === "appointment" && hasAppointmentCard && (
        <AppointmentCard appointment={data.appointment || {}}
          attachment={data.appointment_attachment}
          applicationNumber={data.application_number}
          theme={theme} lang={lang} dir={dir} />
      )}

      {/* ─── MUAQQIB TAB ─── */}
      {activeTab === "muaqqib" && showMuaqqibTab && (
        <MuaqqibInfoBlock applicationNumber={data.application_number}
          theme={theme} lang={lang} dir={dir} />
      )}

      {/* ─── DOCS TAB ─── office-requested documents (numbered list) */}
      {activeTab === "docs" && showDocsTab && (
        <DocsRequestList
          docRequests={docRequests}
          applicationNumber={data.application_number}
          mobileNumber={data.client_mobile_number || ""}
          theme={theme} lang={lang} dir={dir}
          onUploaded={reload}
        />
      )}

      {/* ─── NOTES TAB ─── */}
      {activeTab === "notes" && generalNotes.length > 0 && (
        <section style={css.notesWrap} data-testid="track-notes-general">
          <div style={css.notesTitle}>{T.notes_title}</div>
          {generalNotes.map((n) => (
            <div key={n.id} style={css.noteCard}>
              <div style={css.noteBody}>{n.body}</div>
              <div style={css.noteTime}>{fmtDateTime(n.created_at)}</div>
            </div>
          ))}
        </section>
      )}

      {/* ── First-visit popup for office document requests ────────────── */}
      {docPopupOpen && pendingDocs.length > 0 && (
        <DocsRequestPopup
          count={pendingDocs.length}
          theme={theme} lang={lang} dir={dir}
          onOpenList={() => { closeDocPopup(); setTab("docs"); }}
          onLater={closeDocPopup}
        />
      )}
    </>
  );
}

/* ────────────────── Horizontal Timeline ────────────────── */
function HorizontalTimeline({ currentIdx, isRejected, theme, lang, dir, notesByStage, applicationNumber, T, stageDefs }) {
  const [expanded, setExpanded] = useState(null);
  const scrollRef = useRef(null);

  // Auto-scroll to active stage
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const items = el.querySelectorAll("[data-stage-circle]");
    const target = items[currentIdx];
    if (target) {
      const left = target.offsetLeft - el.clientWidth / 2 + target.clientWidth / 2;
      el.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
    }
  }, [currentIdx]);

  const css = makeStyles(theme);

  return (
    <>
      <style>{`
        @keyframes vta-pulse-ring {
          0%   { box-shadow: 0 0 0 0 rgba(212,175,55,0.55); }
          70%  { box-shadow: 0 0 0 16px rgba(212,175,55,0); }
          100% { box-shadow: 0 0 0 0 rgba(212,175,55,0); }
        }
        @keyframes vta-glow {
          0%,100% { filter: brightness(1.0); }
          50%     { filter: brightness(1.18); }
        }
      `}</style>
      <div ref={scrollRef} style={css.timelineRail} data-testid="timeline-rail">
        {stageDefs.map((stage, idx) => {
          const Icon = stage.icon;
          const reached = idx <= currentIdx && !isRejected;
          const isActive = idx === currentIdx && !isRejected;
          const isDone = idx < currentIdx && !isRejected;
          const hasNote = (notesByStage[stage.key]?.length || 0) > 0;
          const open = expanded === stage.key;

          // Color logic
          const ringColor = isRejected ? "#dc2626" : reached ? theme.gold : theme.borderSoft;
          const fillBg = isActive ? theme.goldGradient : isDone ? (theme.name === "dark" ? "rgba(212,175,55,0.18)" : "rgba(184,144,42,0.16)") : "transparent";
          const iconColor = isActive ? (theme.name === "dark" ? "#0a0a0a" : "#fff") : reached ? theme.gold : theme.textMuted;
          const labelColor = reached ? theme.text : theme.textMuted;

          return (
            <div key={stage.key} style={css.stageCol}>
              {/* Connector line BEFORE this circle (not for first) */}
              {idx > 0 && (
                <div style={{
                  ...css.connector,
                  background: idx <= currentIdx && !isRejected ? theme.gold : theme.borderSoft,
                }} />
              )}
              <button
                type="button"
                data-stage-circle
                onClick={() => hasNote && setExpanded(open ? null : stage.key)}
                style={{
                  ...css.circle(isActive, hasNote),
                  border: `2px solid ${ringColor}`,
                  background: fillBg,
                  color: iconColor,
                  animation: isActive ? "vta-pulse-ring 1.8s ease-out infinite, vta-glow 2s ease-in-out infinite" : "none",
                  cursor: hasNote ? "pointer" : "default",
                }}
                data-testid={`stage-${stage.key}`}
                aria-label={stage.label[lang]}
              >
                {isDone && !hasNote
                  ? <CheckCircle2 size={20} strokeWidth={2.4} />
                  : <Icon size={20} strokeWidth={2.2} />
                }
                {hasNote && <span style={css.noteDot} />}
              </button>
              <div style={{ ...css.stageLabel, color: labelColor, fontWeight: isActive ? 900 : 700 }}>
                {stage.label[lang]}
              </div>
              {open && hasNote && (
                <div style={css.stageNoteCard} data-testid={`stage-note-${stage.key}`}>
                  {notesByStage[stage.key].map((n) => (
                    <StageNote
                      key={n.id} note={n} T={T} theme={theme} dir={dir}
                      applicationNumber={applicationNumber}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

const DOC_TYPE_LABELS = {
  passport: "صورة الجواز",
  national_id: "صورة الهوية الوطنية",
  personal_photo: "صورة شخصية",
  marriage_certificate: "عقد الزواج",
  family_ledger: "كرت العائلة",
  bank_statement: "كشف حساب بنكي",
  employment_letter: "خطاب من جهة العمل",
  receipt: "إيصال التحويل",
  other: "مستند آخر",
};
const docTypeLabel = (k) => DOC_TYPE_LABELS[k] || k || "مستند";

function StageNote({ note, T, theme, dir, applicationNumber }) {
  const [busy, setBusy] = useState(false);
  const [uploaded, setUploaded] = useState(!!note.document_uploaded_at);
  const [err, setErr] = useState("");
  const ref = useRef(null);
  const css = noteStyles(theme);

  const onPick = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("note_id", note.id);
      fd.append("file", file);
      await axios.post(`${API}/track/${applicationNumber}/reupload`, fd);
      setUploaded(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر الرفع");
    } finally { setBusy(false); }
  };

  return (
    <div dir={dir} style={css.card}>
      <div style={css.head}>
        <span style={css.label}>{T.stage_note_label}</span>
        <span style={css.time}>{fmtDateTime(note.created_at)}</span>
      </div>
      <div style={css.body}>{note.body}</div>
      {note.requires_document_type && (
        <div style={css.docTypeBadge} data-testid={`required-doc-${note.id}`}>
          <FileText size={14} strokeWidth={2.4} />
          <div>
            <div style={{ fontSize: 10, opacity: 0.75, marginBottom: 2 }}>المستند المطلوب</div>
            <div style={{ fontWeight: 900, fontSize: 13 }}>{docTypeLabel(note.requires_document_type)}</div>
          </div>
        </div>
      )}
      {note.requires_document_type && (
        <div style={css.reuploadZone}>
          {uploaded ? (
            <span style={css.uploadedTag}>
              <CheckCircle2 size={14} strokeWidth={2.4} /> {T.reupload_done}
            </span>
          ) : (
            <>
              <button
                onClick={() => ref.current?.click()}
                disabled={busy}
                style={css.reuploadBtn}
                data-testid={`reupload-${note.id}`}
              >
                {busy ? <RefreshCw size={14} className="vta-spin" /> : <Upload size={14} strokeWidth={2.2} />}
                {T.reupload_label}
              </button>
              <input
                ref={ref} type="file"
                accept="image/png,image/jpeg,application/pdf"
                style={{ display: "none" }}
                onChange={(e) => onPick(e.target.files?.[0])}
              />
              {err && <div style={css.err}>{err}</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ────────────────── Invoice Card (Pricing Stage) ────────────────── */
function InvoiceCard({ data, theme, lang, dir, onUploaded }) {
  const T = useTrackT(lang);
  const css = makeStyles(theme);
  const items = data.pricing_items || [];
  // Server now sends subtotal_amount + discount_amount + final_total — fallback
  // to local computation only when older responses come back without them.
  const subtotal = (typeof data.subtotal_amount === "number")
    ? Number(data.subtotal_amount)
    : items.reduce((s, it) => s + Number(it.amount || 0), 0);
  const discountPercent = Number(data.discount_percent || 0);
  const discountAmount = Number(data.discount_amount || 0);
  const finalTotal = (typeof data.final_total === "number")
    ? Number(data.final_total)
    : subtotal - discountAmount;
  const hasReceipt = data.payment?.status === "pending" || data.payment?.status === "approved";
  const receiptRejected = data.payment?.status === "rejected" || data.current_status_code === "payment_rejected";
  const isApproved = data.payment?.status === "approved";

  const navigate = useNavigate();

  // Bright termination stamp on the invoice itself. The office puts this
  // on V1 the moment a revision (V2) is issued. ZATCA-style audit signal.
  const isTerminated = ["cancelled", "refunded"].includes(data.invoice_status || "");
  const stampLabel = data.invoice_status === "refunded"
    ? (lang === "en" ? "REFUNDED" : "مسترجعة بالكامل")
    : (lang === "en" ? "CANCELLED" : "ملغية - تم التعديل");

  return (
    <section style={css.invoiceCard} data-testid="invoice-card">
      {isTerminated && (
        <div style={{
          position: "absolute", top: 12, insetInlineEnd: 12,
          padding: "6px 12px", borderRadius: 6,
          border: "2.5px solid #dc2626", color: "#dc2626",
          fontWeight: 900, fontSize: 13, letterSpacing: 1,
          transform: "rotate(-12deg)",
          background: "rgba(220,38,38,0.06)",
          textTransform: "uppercase",
          zIndex: 5,
        }} data-testid="invoice-stamp">
          {stampLabel}
        </div>
      )}
      <div style={css.invoiceHead}>
        <div style={css.invoiceTitleRow}>
          <Receipt size={20} strokeWidth={2.2} style={{ color: theme.gold }} />
          <span style={css.invoiceTitle}>{T.invoice_title}</span>
        </div>
        <div style={css.invoiceHint}>
          {isApproved
            ? (lang === "ar"
                ? <>تم اعتماد الفاتورة برقم مرجعي: <strong dir="ltr">{data.invoice_ref_number || data.application_number}</strong></>
                : <>Invoice approved — ref: <strong dir="ltr">{data.invoice_ref_number || data.application_number}</strong></>)
            : (lang === "ar"
                ? "فيما يلي تفصيل الأتعاب — يرجى المراجعة قبل السداد"
                : "Detailed pricing — please review before paying")}
        </div>
      </div>

      <div style={css.invoiceLines}>
        {items.map((it, idx) => (
          <div key={it.id || idx} style={css.invoiceLine} data-testid={`invoice-line-${idx}`}>
            <span style={css.invoiceLabel}>
              <span style={css.invoiceLineNum}>{idx + 1}</span>
              {it.label}
            </span>
            <span style={css.invoiceAmount} dir="ltr">
              {fmtNum(it.amount)} <RiyalSymbol size={12} />
            </span>
          </div>
        ))}
      </div>

      {/* Subtotal — only shown when a discount exists */}
      {discountAmount > 0 && (
        <div style={css.invoiceSubtotal} data-testid="invoice-subtotal">
          <span>{lang === "ar" ? "الإجمالي قبل الخصم" : "Subtotal"}</span>
          <span dir="ltr">{fmtNum(subtotal)} <RiyalSymbol size={12} /></span>
        </div>
      )}

      {/* Discount badge — transparent breakdown for the client */}
      {discountAmount > 0 && (
        <div style={css.discountBox} data-testid="invoice-discount">
          <div style={css.discountBadge}>
            <Tag size={14} strokeWidth={2.4} />
            <span style={{ fontWeight: 900 }}>
              {lang === "ar"
                ? `تم تطبيق خصم ${fmtNum(discountPercent)}%`
                : `Discount applied ${fmtNum(discountPercent)}%`}
            </span>
          </div>
          <span style={css.discountAmount} dir="ltr">
            − {fmtNum(discountAmount)} <RiyalSymbol size={12} color="#dc2626" />
          </span>
        </div>
      )}

      <div style={css.invoiceTotal}>
        <span>{lang === "ar" ? "الإجمالي النهائي" : "Total"}</span>
        <span dir="ltr">{fmtNum(finalTotal)} <RiyalSymbol size={14} /></span>
      </div>

      {/* After approval: button to view simplified receipt (in-app + print + PDF) */}
      {isApproved && (
        <button
          type="button"
          onClick={() => navigate(`/portal/track/${data.application_number}/receipt`)}
          style={css.viewReceiptBtn}
          data-testid="view-receipt-btn"
        >
          <Receipt size={16} strokeWidth={2.2} />
          {lang === "ar" ? "عرض الإيصال" : "View Receipt"}
        </button>
      )}

      {!hasReceipt && (
        <>
          <div style={css.warningBox} data-testid="warning-24h">
            <AlertCircle size={14} strokeWidth={2.2} />
            <span>{T.warning_24h}</span>
          </div>
          <ReceiptUploader
            applicationNumber={data.application_number}
            theme={theme} T={T} dir={dir} lang={lang}
            rejectedReason={receiptRejected ? data.payment?.review_note : null}
            onUploaded={onUploaded}
          />
        </>
      )}
      {hasReceipt && data.payment?.status === "pending" && (
        <>
          <div style={css.receiptUploaded} data-testid="receipt-uploaded">
            <CheckCircle2 size={14} strokeWidth={2.4} />
            {T.receipt_uploaded}
          </div>
          <UploadedReceiptPreview payment={data.payment} theme={theme} lang={lang} />
        </>
      )}

      {/* Policy is rendered ONCE — inside ReceiptUploader (where the client
          signs it). Removed from here to avoid the duplicated card. */}
    </section>
  );
}

/* ─── UploadedReceiptPreview ───
   After the client uploads their bank-transfer receipt we show them an
   inline preview so they can verify the file actually arrived. PDFs render
   as a labelled "Open PDF" button; images preview directly. The "Open in
   new tab" link uses `target="_blank"` + a fully-qualified URL so React
   Router does NOT swallow the click and bounce the user to the home page. */
function UploadedReceiptPreview({ payment, theme, lang }) {
  const url = payment?.receipt_file_url;
  if (!url) return null;
  const isPdf = (payment?.receipt_mime || "").includes("pdf") || /\.pdf($|\?)/i.test(url);
  const fullUrl = url.startsWith("http")
    ? url
    : `${process.env.REACT_APP_BACKEND_URL}${url}`;
  const labelView = lang === "ar" ? "عرض الإيصال المرفوع" : "View uploaded receipt";
  const labelOpen = lang === "ar" ? "فتح في نافذة جديدة" : "Open in new tab";
  return (
    <div style={{
      marginTop: 10, padding: 12, borderRadius: 12,
      border: `1px dashed ${theme.borderAccent}`,
      background: theme.surface,
    }} data-testid="uploaded-receipt-preview">
      <div style={{
        fontSize: 11, color: theme.textMuted, fontWeight: 800,
        marginBottom: 8, letterSpacing: 0.4,
      }}>
        {labelView}
      </div>
      {isPdf ? (
        <a
          href={fullUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "10px 14px", borderRadius: 10,
            background: theme.goldGradient,
            color: theme.name === "dark" ? "#0a0a0a" : "#fff",
            fontWeight: 900, fontSize: 13, textDecoration: "none",
          }}
          data-testid="uploaded-receipt-open"
        >
          <FileText size={14} strokeWidth={2.4} />
          {labelOpen}
          <ExternalLink size={12} strokeWidth={2.2} />
        </a>
      ) : (
        <a
          href={fullUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ display: "block" }}
          data-testid="uploaded-receipt-open"
        >
          <img
            src={fullUrl}
            alt="receipt"
            style={{
              display: "block", maxWidth: "100%", maxHeight: 320,
              margin: "0 auto", borderRadius: 8, objectFit: "contain",
              border: `1px solid ${theme.borderSoft}`,
            }}
          />
          <div style={{
            marginTop: 8, fontSize: 11, color: theme.gold,
            fontWeight: 800, textAlign: "center",
          }}>
            {labelOpen} ↗
          </div>
        </a>
      )}
    </div>
  );
}

/* ─── Internal Payment Card (office-submitted apps only) ───
   The client never sees bank details or the receipt uploader here. The
   payment was/is collected directly at the office (cash, POS, transfer
   recorded by the staff). Once the staff records the payment, the client
   sees an explicit "View Receipt" CTA. Until then we just confirm the
   total and reassure them that no upload is needed. */
function InternalPaymentCard({ data, theme, lang }) {
  const navigate = useNavigate();
  const css = makeStyles(theme);
  const items = data.pricing_items || [];
  const finalTotal = (typeof data.final_total === "number")
    ? Number(data.final_total)
    : items.reduce((s, it) => s + Number(it.amount || 0), 0);
  const isApproved = data.payment?.status === "approved" || data.current_status_code === "payment_approved"
    || (data.current_status_code && data.current_status_code !== "fees_set"
        && data.current_status_code !== "awaiting_payment"
        && data.current_status_code !== "payment_rejected");
  return (
    <section style={css.invoiceCard} data-testid="internal-payment-card">
      <div style={css.invoiceHead}>
        <div style={css.invoiceTitleRow}>
          <Receipt size={20} strokeWidth={2.2} style={{ color: theme.gold }} />
          <span style={css.invoiceTitle}>
            {lang === "ar" ? "تفاصيل المعاملة" : "Transaction Details"}
          </span>
        </div>
        <div style={css.invoiceHint}>
          {isApproved
            ? (lang === "ar"
                ? "تم تحصيل الرسوم في المكتب — يمكنك تحميل وطباعة الإيصال أدناه"
                : "Fees collected at the office — you can download & print the receipt below")
            : (lang === "ar"
                ? "هذه معاملة مكتبية — التحصيل يتم في المكتب مباشرة عند زيارتك"
                : "This is an in-office transaction — payment is collected at the office during your visit")}
        </div>
      </div>

      <div style={css.invoiceLines}>
        {items.map((it, idx) => (
          <div key={it.id || idx} style={css.invoiceLine}>
            <span style={css.invoiceLabel}>
              <span style={css.invoiceLineNum}>{idx + 1}</span>
              {it.label}
            </span>
            <span style={css.invoiceAmount} dir="ltr">
              {fmtNum(it.amount)} <RiyalSymbol size={12} />
            </span>
          </div>
        ))}
      </div>

      <div style={css.invoiceTotal}>
        <span>{lang === "ar" ? "الإجمالي" : "Total"}</span>
        <span dir="ltr">{fmtNum(finalTotal)} <RiyalSymbol size={14} /></span>
      </div>

      {isApproved ? (
        <button
          type="button"
          onClick={() => navigate(`/portal/track/${data.application_number}/receipt`)}
          style={css.viewReceiptBtn}
          data-testid="internal-view-receipt-btn"
        >
          <Receipt size={16} strokeWidth={2.2} />
          {lang === "ar" ? "عرض الإيصال" : "View Receipt"}
        </button>
      ) : (
        <div style={{
          marginTop: 14, padding: "10px 12px",
          background: "rgba(212,175,55,0.08)",
          border: `1px solid ${theme.gold}33`,
          borderRadius: 10, fontSize: 12, color: theme.textMuted,
          display: "flex", alignItems: "center", gap: 8,
        }} data-testid="internal-awaiting-collection">
          <AlertCircle size={14} strokeWidth={2.2} />
          <span>
            {lang === "ar"
              ? "في انتظار تأكيد التحصيل من قِبل المكتب — لا حاجة لرفع أي شيء"
              : "Awaiting confirmation from the office — no upload required"}
          </span>
        </div>
      )}
    </section>
  );
}

/* Numbered display of the official 6-point service policy. Used at the
   bottom of the invoice card, the receipt-upload page, the receipt itself
   and the portal landing — single source of truth. */
function PolicyCard({ disclaimer, lang, theme, testId }) {
  const points = (lang === "ar" ? disclaimer.points_ar : disclaimer.points_en) || [];
  const title = lang === "ar" ? (disclaimer.title_ar || "سياسة الخدمة والفواتير")
                              : (disclaimer.title_en || "Service & Invoice Policy");
  return (
    <div data-testid={testId} style={{
      background: theme.surface, border: `1px solid ${theme.borderSoft}`,
      borderRadius: 12, padding: "12px 14px", marginTop: 12,
      fontSize: 12, lineHeight: 1.85, color: theme.text,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <AlertCircle size={14} strokeWidth={2.4} color={theme.gold} />
        <strong style={{ fontSize: 13, color: theme.gold }}>{title}</strong>
      </div>
      <ol style={{ margin: 0, paddingInlineStart: 20 }}>
        {points.map((p, i) => (
          <li key={i} style={{ marginBottom: 4 }}>{p}</li>
        ))}
      </ol>
    </div>
  );
}

function ReceiptUploader({ applicationNumber, theme, T, dir, lang, rejectedReason, onUploaded }) {
  const css = makeStyles(theme);
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoResult, setPromoResult] = useState(null);
  const applyPromo = async () => {
    try {
      const r = await axios.post(API + "/promo-codes/apply", { application_number: applicationNumber, code: promoCode });
      setPromoResult(r.data); setErr("");
    } catch (e) { setErr(e.response?.data?.detail || "كود الخصم غير صالح"); }
  };
  const [banks, setBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState("");
  const [copied, setCopied] = useState("");
  const [disclaimer, setDisclaimer] = useState(null);
  const [policyAck, setPolicyAck] = useState(false);

  useEffect(() => {
    axios.get(`${API}/banks`).then((r) => {
      const items = r.data?.items || [];
      setBanks(items);
      if (items.length > 0) setSelectedBankId(items[0].id);
    }).catch(() => { /* fail soft — keep upload working */ });
    axios.get(`${API}/disclaimer`).then((r) => setDisclaimer(r.data)).catch(() => undefined);
  }, []);

  const selectedBank = banks.find((b) => b.id === selectedBankId) || banks[0];

  const copy = (text, key) => {
    if (!text) return;
    try {
      navigator.clipboard.writeText(String(text));
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch { /* ignore */ }
  };

  const pick = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setErr("الحد الأقصى للملف 15 ميجا");
      return;
    }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/track/${applicationNumber}/upload-receipt`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (onUploaded) onUploaded();
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر رفع الإيصال");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div>
      {/* Multi-bank picker — show all enabled banks the office accepts */}
      {banks.length > 0 && (
        <div style={css.banksWrap} data-testid="banks-picker">
          <div style={css.banksLabel}>
            <Building2 size={14} strokeWidth={2.4} />
            {lang === "ar" ? "اختر البنك للتحويل" : "Select bank for transfer"}
          </div>
          <div style={css.banksList}>
            {banks.map((b) => {
              const active = b.id === selectedBankId;
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setSelectedBankId(b.id)}
                  style={{ ...css.bankPill, ...(active ? css.bankPillActive : {}) }}
                  data-testid={`bank-pill-${b.bank_code || b.id}`}
                >
                  {b.logo_url && (
                    <div style={{
                      width: 24, height: 24, borderRadius: "50%",
                      background: "rgba(255,255,255,0.95)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      overflow: "hidden", flexShrink: 0,
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}>
                      <img
                        src={b.logo_url.startsWith("http") ? b.logo_url : `${process.env.REACT_APP_BACKEND_URL}${b.logo_url}`}
                        alt={b.bank_name}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        style={{ width: "78%", height: "78%", objectFit: "contain" }}
                      />
                    </div>
                  )}
                  <span>{b.bank_name}</span>
                </button>
              );
            })}
          </div>
          {selectedBank && (
            <div style={css.bankDetailsCard} data-testid={`bank-details-${selectedBank.id}`}>
              <BankField label={lang === "ar" ? "اسم الحساب" : "Account name"} value={selectedBank.account_name}
                onCopy={() => copy(selectedBank.account_name, "name")} copied={copied === "name"} theme={theme} />
              <BankField label={lang === "ar" ? "رقم الحساب" : "Account number"} value={selectedBank.account_number}
                onCopy={() => copy(selectedBank.account_number, "acc")} copied={copied === "acc"} theme={theme} mono />
              <BankField label="IBAN" value={selectedBank.iban}
                onCopy={() => copy(selectedBank.iban, "iban")} copied={copied === "iban"} theme={theme} mono />
              {selectedBank.swift && (
                <BankField label="SWIFT" value={selectedBank.swift}
                  onCopy={() => copy(selectedBank.swift, "swift")} copied={copied === "swift"} theme={theme} mono />
              )}
              {(lang === "ar" ? selectedBank.instructions_ar : selectedBank.instructions_en) && (
                <div style={css.bankInstructions}>
                  {lang === "ar" ? selectedBank.instructions_ar : selectedBank.instructions_en}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {rejectedReason && (
        <div style={{ ...css.warningBox, background: "rgba(220,38,38,0.10)", borderColor: "rgba(220,38,38,0.4)", color: "#dc2626" }} data-testid="receipt-rejected">
          <AlertCircle size={14} strokeWidth={2.2} />
          <span>{rejectedReason}</span>
        </div>
      )}
      {err && (
        <div style={{ ...css.warningBox, background: "rgba(220,38,38,0.10)", borderColor: "rgba(220,38,38,0.4)", color: "#dc2626", marginBottom: 8 }} data-testid="receipt-error">
          <AlertCircle size={14} strokeWidth={2.2} />
          <span>{err}</span>
        </div>
      )}
      {/* Show the full 6-point service policy BEFORE the receipt upload
          button so the client implicitly acknowledges it by uploading. */}
      {disclaimer && (
        <PolicyCard disclaimer={disclaimer} lang={lang} theme={theme}
          testId="upload-disclaimer" />
      )}
      {disclaimer && (
        <label
          style={{
            display: "flex", alignItems: "center", gap: 8, fontSize: 12,
            color: theme.text, cursor: "pointer", margin: "10px 0",
            padding: "8px 10px", borderRadius: 8, background: theme.surface,
            border: `1px solid ${theme.borderSoft}`,
          }}
          data-testid="policy-ack-upload"
        >
          <input type="checkbox" checked={policyAck}
            onChange={(e) => setPolicyAck(e.target.checked)}
            data-testid="policy-ack-upload-checkbox" />
          <span>
            {lang === "ar"
              ? "أُقرّ بأنني اطّلعت على سياسة الخدمة وتفاصيل الفاتورة وأوافق عليها."
              : "I confirm I have reviewed the service policy and invoice details and I agree."}
          </span>
        </label>
      )}
      <div data-testid="promo-code-box" style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} placeholder={dir === "rtl" ? "كود الخصم" : "Promo code"} style={{ flex: 1, padding: 10, border: "1px solid rgb(203,213,225)", borderRadius: 8 }} />
        <button type="button" onClick={applyPromo} style={{ ...css.uploadBtn, width: "auto" }}>{dir === "rtl" ? "تطبيق" : "Apply"}</button>
      </div>
      {promoResult && <div style={{ color: "green", marginBottom: 10 }}>تم الخصم {promoResult.discount_amount} ر.س — الإجمالي {promoResult.final_total} ر.س</div>}
      <button
        type="button"
        disabled={busy || (disclaimer && !policyAck)}
        onClick={() => ref.current?.click()}
        style={{
          ...css.uploadBtn,
          opacity: (busy || (disclaimer && !policyAck)) ? 0.55 : 1,
          cursor: busy ? "wait" : ((disclaimer && !policyAck) ? "not-allowed" : "pointer"),
        }}
        data-testid="upload-receipt-btn"
      >
        <Upload size={16} strokeWidth={2.2} />
        {busy ? (dir === "rtl" ? "جاري الرفع…" : "Uploading…") : T.upload_receipt}
      </button>
      <input
        ref={ref}
        type="file"
        accept="image/png,image/jpeg,image/webp,application/pdf"
        style={{ display: "none" }}
        onChange={(e) => pick(e.target.files?.[0])}
        data-testid="receipt-input"
      />
    </div>
  );
}

/* ────────────────── Appointment Card ────────────────── */
function AppointmentCard({ appointment, attachment, applicationNumber, theme, lang, dir }) {
  const T = useTrackT(lang);
  const css = makeStyles(theme);
  const status = appointment.status || "booked";
  const colors = {
    booked: { bg: "rgba(22,163,74,0.10)", border: "rgba(22,163,74,0.45)", text: "#16a34a", label: T.appt_status_booked },
    rescheduled: { bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.45)", text: "#d97706", label: T.appt_status_rescheduled },
    cancelled: { bg: "rgba(220,38,38,0.10)", border: "rgba(220,38,38,0.45)", text: "#dc2626", label: T.appt_status_cancelled },
    done: { bg: "rgba(22,163,74,0.10)", border: "rgba(22,163,74,0.45)", text: "#16a34a", label: T.appt_status_done },
  }[status] || { bg: theme.surface, border: theme.borderSoft, text: theme.text, label: status };

  return (
    <section style={{
      ...css.invoiceCard,
      background: colors.bg, borderColor: colors.border,
    }} data-testid="appointment-card">
      <div style={css.invoiceHead}>
        <div style={css.invoiceTitleRow}>
          <Calendar size={20} strokeWidth={2.2} style={{ color: colors.text }} />
          <span style={{ ...css.invoiceTitle, color: colors.text }}>{T.appt_title}</span>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 900, padding: "4px 10px",
          background: colors.text, color: "#fff", borderRadius: 999, letterSpacing: 0.4,
        }} data-testid="appointment-status">{colors.label}</span>
      </div>
      <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
        {appointment.visa_center && (
          <ApptRow icon={<MapPin size={14} />} label={T.appt_center} value={appointment.visa_center} theme={theme} />
        )}
        {appointment.city && (
          <ApptRow icon={<MapPin size={14} />} label={T.appt_city} value={appointment.city} theme={theme} />
        )}
        {appointment.appointment_date && (() => {
          // Split stored value (YYYY-MM-DD or YYYY-MM-DDTHH:mm[:ss]) into date + time (24h).
          const v = String(appointment.appointment_date);
          const dateStr = v.slice(0, 10);
          const timeRaw = v.length > 10 ? v.slice(11, 16) : "";
          return (
            <>
              <ApptRow icon={<Calendar size={14} />} label={T.appt_date}
                value={<span dir="ltr">{dateStr}</span>} theme={theme} />
              {timeRaw && (
                <ApptRow icon={<Calendar size={14} />} label={T.appt_time}
                  value={<span dir="ltr">{timeRaw}</span>} theme={theme} />
              )}
            </>
          );
        })()}
        {appointment.reference_number && (
          <ApptRow icon={<FileText size={14} />} label={T.appt_ref} value={<span dir="ltr">{appointment.reference_number}</span>} theme={theme} />
        )}
      </div>
      {appointment.pdf_attachment_id && appointment.pdf_url && (
        <a
          href={appointment.pdf_url}
          target="_blank" rel="noopener noreferrer"
          style={css.pdfBtn}
          data-testid="appointment-pdf"
        >
          <FileText size={16} strokeWidth={2.2} />
          <span>{T.appt_pdf}</span>
          <ExternalLink size={12} strokeWidth={2.2} />
        </a>
      )}
      {/* Office-uploaded attachment (works for both office-submitted apps and client-submitted apps) */}
      {attachment && applicationNumber && (
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/track/${applicationNumber}/appointment-attachment`}
          target="_blank" rel="noopener noreferrer"
          download={attachment.filename}
          style={{ ...css.pdfBtn, marginTop: 10 }}
          data-testid="appointment-attachment-download"
        >
          <FileText size={16} strokeWidth={2.2} />
          <span>
            {lang === "ar" ? "تحميل ملف الموعد" : "Download appointment file"}
            <span style={{ fontSize: 10, color: "#fff", opacity: 0.8, marginInlineStart: 8 }} dir="ltr">
              {attachment.filename}
            </span>
          </span>
          <ExternalLink size={12} strokeWidth={2.2} />
        </a>
      )}
    </section>
  );
}

function BankField({ label, value, onCopy, copied, theme, mono }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      fontSize: 12, padding: "6px 4px", borderBottom: `1px dashed ${theme.borderSoft}`,
    }} data-testid={`bank-field-${label}`}>
      <span style={{ color: theme.textMuted, fontWeight: 700 }}>{label}</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <span dir={mono ? "ltr" : undefined}
          style={{ fontFamily: mono ? "monospace" : "inherit", fontWeight: 800, color: theme.text }}>
          {value}
        </span>
        <button onClick={onCopy} title="نسخ" type="button"
          style={{ background: copied ? "#16a34a" : "transparent", border: "none",
            color: copied ? "#fff" : theme.textMuted, cursor: "pointer", padding: 4,
            borderRadius: 6, display: "inline-flex" }}>
          {copied ? <CheckCircle2 size={14} strokeWidth={2.4} /> : <Copy size={14} strokeWidth={2.2} />}
        </button>
      </span>
    </div>
  );
}


function ApptRow({ icon, label, value, theme }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      background: theme.surface, border: `1px solid ${theme.borderSoft}`,
      padding: "10px 12px", borderRadius: 10,
    }}>
      <span style={{ color: theme.gold, display: "inline-flex" }}>{icon}</span>
      <span style={{ fontSize: 11, color: theme.textMuted, minWidth: 80 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 800, flex: 1, textAlign: "end" }}>{value}</span>
    </div>
  );
}

// Tiny helper to access the T strings from inside InvoiceCard/AppointmentCard
function useTrackT(lang) {
  return lang === "ar"
    ? {
        invoice_title: "فاتورة المعاملة",
        invoice_hint: "فيما يلي تفصيل الأتعاب — اعتمدت بواسطة الإدارة",
        invoice_total: "الإجمالي",
        invoice_currency: "ريال",
        upload_receipt: "رفع إيصال السداد",
        receipt_uploaded: "تم رفع الإيصال — بانتظار الاعتماد",
        warning_24h: "ملاحظة: تُلغى المعاملة خلال 24 ساعة في حال عدم السداد.",
        appt_title: "تفاصيل الموعد",
        appt_center: "المركز",
        appt_city: "المدينة",
        appt_date: "التاريخ",
        appt_time: "الساعة",
        appt_ref: "الرقم المرجعي",
        appt_pdf: "تنزيل ملف الموعد (PDF)",
        appt_status_booked: "مؤكد",
        appt_status_rescheduled: "تم تأجيله",
        appt_status_cancelled: "ملغي",
        appt_status_done: "تم",
      }
    : {
        invoice_title: "Invoice",
        invoice_hint: "Detailed pricing breakdown — approved by management",
        invoice_total: "Total",
        invoice_currency: "SAR",
        upload_receipt: "Upload payment receipt",
        receipt_uploaded: "Receipt uploaded — awaiting approval",
        warning_24h: "Note: the application is cancelled after 24h if no payment is uploaded.",
        appt_title: "Appointment details",
        appt_center: "Center",
        appt_city: "City",
        appt_date: "Date",
        appt_time: "Time",
        appt_ref: "Reference number",
        appt_pdf: "Download appointment PDF",
        appt_status_booked: "Confirmed",
        appt_status_rescheduled: "Rescheduled",
        appt_status_cancelled: "Cancelled",
        appt_status_done: "Completed",
      };
}

/* ────────────────── Small UI helpers ────────────────── */
function Meta({ label, value, theme }) {
  return (
    <div style={{
      background: theme.surface, border: `1px solid ${theme.borderSoft}`,
      borderRadius: 10, padding: "8px 12px",
      display: "flex", flexDirection: "column", gap: 2, minWidth: 120,
    }}>
      <div style={{ fontSize: 10, color: theme.textMuted, letterSpacing: "0.4px" }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 800 }}>{value}</div>
    </div>
  );
}

/* ────────────────── Styles ────────────────── */
const makeStyles = (theme) => ({
  page: {
    maxWidth: 720, margin: "0 auto",
    padding: "20px 16px 80px",
    fontFamily: "'Tajawal','Segoe UI',sans-serif",
    color: theme.text,
    position: "relative",
    zIndex: 1,
  },
  backBtn: {
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "8px 14px", borderRadius: 999,
    background: "transparent",
    border: `1px solid ${theme.borderAccent}`,
    color: theme.gold, cursor: "pointer",
    fontSize: 13, fontWeight: 700, fontFamily: "inherit",
    marginBottom: 14,
  },
  header: { textAlign: "center", marginBottom: 18 },
  h1: { margin: 0, fontSize: 26, fontWeight: 900, color: theme.gold },
  sub: { fontSize: 13, color: theme.textMuted, marginTop: 6 },
  searchRow: {
    display: "flex", gap: 10, alignItems: "stretch",
    maxWidth: 560, margin: "0 auto 18px",
  },
  inputWrap: { position: "relative", flex: 1, display: "flex", alignItems: "center" },
  searchIcon: { position: "absolute", insetInlineStart: 12, color: theme.textMuted, pointerEvents: "none" },
  input: {
    width: "100%", padding: "12px 14px", paddingInlineStart: 38,
    borderRadius: 10, border: `1px solid ${theme.inputBorder}`,
    background: theme.inputBg, color: theme.text,
    fontSize: 14, fontFamily: "inherit", letterSpacing: "1px", boxSizing: "border-box",
  },
  btn: {
    padding: "12px 22px", background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none", borderRadius: 10, fontWeight: 900, fontSize: 13,
    cursor: "pointer",
  },
  err: {
    display: "flex", gap: 8, alignItems: "center",
    background: theme.errorBg, border: `1px solid ${theme.errorBorder}`,
    color: theme.errorText, padding: 12, borderRadius: 10, fontSize: 13,
    maxWidth: 560, margin: "0 auto 16px",
  },
  hero: {
    background: theme.surfaceCard, border: `1px solid ${theme.borderAccent}`,
    borderRadius: 16, padding: "14px 16px", marginBottom: 10,
    boxShadow: theme.name === "dark" ? "0 0 0 1px rgba(212,175,55,0.08)" : "0 6px 24px rgba(184,144,42,0.10)",
  },
  heroGreet: { fontSize: 12, color: theme.textSoft, fontWeight: 700, marginBottom: 2 },
  heroNumber: {
    fontFamily: "monospace", fontWeight: 900, fontSize: 17,
    color: theme.gold, letterSpacing: 1.5, marginBottom: 10,
  },
  heroMeta: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 },
  timelineWrap: {
    background: theme.surfaceCard, border: `1px solid ${theme.borderSoft}`,
    borderRadius: 16, padding: "14px 8px 10px", marginBottom: 14,
  },
  timelineTitle: { fontSize: 12, fontWeight: 800, color: theme.gold, marginBottom: 10, padding: "0 8px" },
  timelineRail: {
    display: "flex", alignItems: "flex-start",
    overflowX: "auto", overflowY: "visible",
    padding: "12px 8px 40px",
    gap: 0,
    scrollbarWidth: "thin",
  },
  stageCol: {
    flex: "0 0 auto",
    width: 96,
    display: "flex", flexDirection: "column", alignItems: "center",
    position: "relative",
  },
  connector: {
    position: "absolute",
    top: 22, height: 2,
    insetInlineStart: -48, width: 48 + 24,
    zIndex: 0,
  },
  circle: (active, clickable) => ({
    width: 44, height: 44, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    position: "relative",
    zIndex: 1,
    transition: "background 200ms, color 200ms",
    boxSizing: "border-box",
    padding: 0,
    fontWeight: 900,
    transform: active ? "scale(1.06)" : "scale(1)",
  }),
  noteDot: {
    position: "absolute", top: -2, insetInlineEnd: -2,
    width: 10, height: 10, borderRadius: "50%",
    background: "#dc2626",
    border: `2px solid ${theme.surfaceCard}`,
  },
  stageLabel: {
    marginTop: 8, fontSize: 10.5, textAlign: "center",
    maxWidth: 90, lineHeight: 1.35,
    fontWeight: 700,
  },
  stageNoteCard: {
    position: "absolute",
    top: 110,
    insetInlineStart: "50%",
    transform: "translateX(-50%)",
    width: 320,
    zIndex: 5,
    animation: "slideDown 200ms ease",
  },
  notesWrap: {
    background: theme.surfaceCard, border: `1px solid ${theme.borderSoft}`,
    borderRadius: 16, padding: 18,
  },
  notesTitle: { fontSize: 13, fontWeight: 800, color: theme.gold, marginBottom: 12 },
  noteCard: {
    background: theme.surface, border: `1px solid ${theme.borderSoft}`,
    borderRadius: 10, padding: "10px 12px", marginBottom: 8,
  },
  noteBody: { fontSize: 13, lineHeight: 1.7, color: theme.text },
  noteTime: { fontSize: 10, color: theme.textMuted, marginTop: 6 },
  invoiceCard: {
    background: theme.surfaceCard, border: `1px solid ${theme.borderAccent}`,
    borderRadius: 18, padding: 22, marginBottom: 22, position: "relative",
    boxShadow: theme.name === "dark" ? "0 0 0 1px rgba(212,175,55,0.08)" : "0 6px 24px rgba(184,144,42,0.10)",
  },
  invoiceHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap", marginBottom: 6 },
  invoiceTitleRow: { display: "inline-flex", alignItems: "center", gap: 8 },
  invoiceTitle: { fontSize: 16, fontWeight: 900, color: theme.gold },
  invoiceHint: { fontSize: 12, color: theme.textMuted, marginBottom: 12, lineHeight: 1.6 },
  invoiceLines: {
    display: "flex", flexDirection: "column", gap: 6,
    border: `1px solid ${theme.borderSoft}`, borderRadius: 10, padding: 10, marginBottom: 10,
    background: theme.surface,
  },
  invoiceLine: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "8px 4px", borderBottom: `1px dashed ${theme.borderSoft}`, fontSize: 13,
  },
  invoiceLabel: { fontWeight: 700, color: theme.text, display: "inline-flex", alignItems: "center", gap: 8 },
  invoiceLineNum: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 22, height: 22, borderRadius: "50%",
    background: theme.gold, color: "#0a0a0a",
    fontSize: 11, fontWeight: 900, fontFamily: "monospace",
  },
  invoiceAmount: { fontWeight: 900, color: theme.gold, fontFamily: "monospace" },
  invoiceSubtotal: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "10px 4px", fontSize: 13, color: theme.textMuted, fontFamily: "monospace",
  },
  discountBox: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    background: "rgba(220,38,38,0.10)", border: "1px dashed rgba(220,38,38,0.4)",
    color: "#dc2626", padding: "10px 12px", borderRadius: 10, fontSize: 12, marginBottom: 12,
  },
  discountBadge: {
    display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 800,
  },
  discountAmount: { fontFamily: "monospace", fontWeight: 900 },
  invoiceTotal: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "12px 14px", background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    borderRadius: 12, fontSize: 15, fontWeight: 900, letterSpacing: 0.4, marginBottom: 14,
  },
  warningBox: {
    display: "flex", gap: 8, alignItems: "center",
    background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.45)",
    color: "#d97706", padding: "10px 12px", borderRadius: 10, fontSize: 12, marginBottom: 12,
  },
  uploadBtn: {
    width: "100%", padding: "13px 16px",
    background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none", borderRadius: 12, fontWeight: 900, fontSize: 14,
    cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
  },
  receiptUploaded: {
    display: "inline-flex", alignItems: "center", gap: 8,
    width: "100%", justifyContent: "center",
    background: "rgba(22,163,74,0.12)", border: "1px solid rgba(22,163,74,0.4)",
    color: "#16a34a", padding: "12px 14px", borderRadius: 10, fontWeight: 800, fontSize: 13,
  },
  viewReceiptBtn: {
    width: "100%", padding: "13px 16px", marginBottom: 8,
    background: "transparent", border: `2px solid ${theme.gold}`,
    color: theme.gold, borderRadius: 12, fontWeight: 900, fontSize: 14,
    cursor: "pointer", display: "inline-flex", alignItems: "center",
    justifyContent: "center", gap: 8,
  },
  banksWrap: {
    background: theme.surface, border: `1px solid ${theme.borderSoft}`,
    borderRadius: 12, padding: 12, marginBottom: 12,
  },
  banksLabel: {
    display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 800,
    fontSize: 12, color: theme.textMuted, marginBottom: 8,
  },
  banksList: {
    display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10,
  },
  bankPill: {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: "transparent", color: theme.text,
    border: `1px solid ${theme.border}`, padding: "6px 12px",
    borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer",
  },
  bankPillActive: {
    background: theme.gold, color: "#0a0a0a", borderColor: theme.gold,
  },
  bankDetailsCard: {
    background: theme.bg, border: `1px solid ${theme.borderSoft}`,
    borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 6,
  },
  bankFieldRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    fontSize: 12, padding: "6px 4px", borderBottom: `1px dashed ${theme.borderSoft}`,
  },
  bankInstructions: {
    background: theme.surface, padding: "8px 10px", borderRadius: 8,
    fontSize: 11, color: theme.textMuted, marginTop: 6, lineHeight: 1.7,
  },
  disclaimerBox: {
    display: "flex", gap: 8, alignItems: "flex-start",
    background: theme.surface, border: `1px dashed ${theme.borderSoft}`,
    color: theme.textMuted, padding: "10px 12px", borderRadius: 10,
    fontSize: 11, lineHeight: 1.7, marginTop: 12,
  },
  includesBadge: {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: "rgba(22,163,74,0.10)", border: "1px solid rgba(22,163,74,0.4)",
    color: "#16a34a", padding: "8px 12px", borderRadius: 8, fontSize: 12,
    fontWeight: 800, marginBottom: 10,
  },
  pdfBtn: {
    marginTop: 14, padding: "12px 16px",
    background: theme.surface, border: `1px solid ${theme.borderAccent}`,
    color: theme.gold, borderRadius: 12, fontWeight: 800, fontSize: 13,
    cursor: "pointer", textDecoration: "none",
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%",
  },
});

const noteStyles = (theme) => ({
  card: {
    background: theme.surface, border: `1px solid ${theme.borderAccent}`,
    borderRadius: 12, padding: "12px 14px",
    boxShadow: "0 6px 22px rgba(0,0,0,0.22)",
    textAlign: "start",
  },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 },
  label: { fontSize: 10, fontWeight: 900, color: theme.gold, letterSpacing: "0.4px" },
  time: { fontSize: 10, color: theme.textMuted },
  body: { fontSize: 13, lineHeight: 1.7, color: theme.text, marginBottom: 8 },
  docTypeBadge: {
    display: "flex", gap: 10, alignItems: "center",
    background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    padding: "10px 12px", borderRadius: 10, marginBottom: 8,
    fontFamily: "inherit",
  },
  reuploadZone: { marginTop: 10, borderTop: `1px dashed ${theme.borderSoft}`, paddingTop: 10 },
  reuploadBtn: {
    width: "100%", padding: "9px 12px",
    background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none", borderRadius: 8, fontWeight: 900, fontSize: 12,
    cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
  },
  uploadedTag: {
    display: "inline-flex", alignItems: "center", gap: 6,
    color: "#16a34a", fontWeight: 800, fontSize: 12,
    background: "rgba(22,163,74,0.10)", padding: "8px 12px", borderRadius: 8, width: "100%", justifyContent: "center",
  },
  err: { color: "#dc2626", fontSize: 11, marginTop: 6 },
});


/* ───────────────── Appointment Picker (binding) ───────────────── */
function AppointmentPicker({ applicationNumber, theme, lang, dir, onPicked }) {
  const [state, setState] = useState({ options: [], chosen: null, pickDeadline: null, loaded: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Live countdown ticker — refreshes the rendered deadline every minute.
  const [, setNow] = useState(Date.now());
  useEffect(() => {
    if (!applicationNumber) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/portal/track/${applicationNumber}/appointment-options`)
      .then(({ data }) => setState({
        options: data.options || [],
        chosen: data.chosen || null,
        pickDeadline: data.pick_deadline || null,
        loaded: true,
      }))
      .catch(() => setState({ options: [], chosen: null, pickDeadline: null, loaded: true }));
  }, [applicationNumber]);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  if (!state.loaded) return null;
  if (state.options.length === 0 && !state.chosen) return null;
  const t = (ar, en, ur) => lang === "ur" ? ur : lang === "en" ? en : ar;
  // Compute deadline countdown ("xxh yym remaining") so the client feels
  // the urgency (48h before auto-cancel).
  let remaining = null;
  if (!state.chosen && state.pickDeadline) {
    const ms = new Date(state.pickDeadline).getTime() - Date.now();
    if (ms > 0) {
      const totalMin = Math.floor(ms / 60_000);
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      remaining = { h, m, expired: false };
    } else {
      remaining = { h: 0, m: 0, expired: true };
    }
  }
  const pick = async (optionId, optionLabel) => {
    // Hard confirmation — appointment binding is irreversible from the
    // client side and gates payment, so we make sure the click was
    // intentional. The label gives the client one last visual check.
    const confirmMsg = lang === "en"
      ? `Are you SURE about this appointment?\n\n${optionLabel}\n\nThis choice is final. The office cannot move it without contacting you.`
      : lang === "ur"
        ? `کیا آپ اس وقت کے بارے میں یقینی ہیں؟\n\n${optionLabel}\n\nیہ انتخاب حتمی ہے۔`
        : `هل أنت متأكد من اختيار هذا الموعد؟\n\n${optionLabel}\n\nاختيارك نهائي — لن يتمكّن المكتب من تغييره دون التواصل معك مباشرة.`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/portal/track/${applicationNumber}/appointment-pick`,
        { option_id: optionId },
      );
      setState((s) => ({ ...s, chosen: data.chosen }));
      onPicked?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "خطأ");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section dir={dir} style={{
      background: state.chosen ? "rgba(22,163,74,0.06)" : "rgba(212,175,55,0.06)",
      border: `2px solid ${state.chosen ? "#16a34a" : theme.borderAccent}`,
      borderRadius: 16, padding: 18, marginBottom: 18,
    }} data-testid="appointment-picker">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <CalendarCheck size={18} strokeWidth={2.4} style={{ color: state.chosen ? "#16a34a" : theme.gold }} />
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 900, color: state.chosen ? "#16a34a" : theme.gold, display: "inline-flex", alignItems: "center", gap: 8 }}>
          {state.chosen && <CheckCircle2 size={16} strokeWidth={2.4} />}
          <span>{state.chosen
            ? t("تم تثبيت موعدك", "Your appointment is locked", "آپ کا اپوائنٹمنٹ مقفل ہے")
            : t("اختر موعدك", "Pick your appointment", "اپنا وقت منتخب کریں")}</span>
        </h3>
      </div>
      {state.chosen ? (
        <div style={{ fontSize: 13, lineHeight: 1.8 }}>
          <div style={{ fontWeight: 800, fontSize: 16 }} dir="ltr">
            {new Date(state.chosen.datetime).toLocaleString("en-GB", { timeZone: "Asia/Riyadh", hour12: false })}
          </div>
          {state.chosen.location && (
            <div style={{ marginTop: 4, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <MapPin size={13} strokeWidth={2.2} />
              <span>{state.chosen.location}</span>
            </div>
          )}
          <div style={{ marginTop: 10, padding: 10, background: "rgba(22,163,74,0.10)", borderRadius: 8, fontSize: 11, color: "#16a34a" }}>
            {t("تم تحديد هذا الموعد بناءً على اختيارك وأصبح ملزماً — لا يمكن تغييره.",
               "This appointment was set by your own choice and is now binding — cannot be changed.",
               "آپ کے انتخاب کی بنیاد پر یہ وقت مقرر کیا گیا اور اب پابند ہے — تبدیل نہیں کیا جا سکتا۔")}
          </div>
        </div>
      ) : (
        <>
          <p style={{ fontSize: 12, opacity: 0.85, marginBottom: 12, lineHeight: 1.7 }}>
            {t("اضغط على الموعد المناسب لك. اختيارك إقرار رسمي ولا يمكن تغييره لاحقاً.",
               "Tap the slot that works for you. Your choice is a formal declaration and cannot be changed later.",
               "اپنی موزوں تاریخ پر کلک کریں۔ آپ کا انتخاب پابند ہے اور بعد میں تبدیل نہیں ہوگا۔")}
          </p>
          {remaining && (
            <div style={{
              padding: "10px 12px", borderRadius: 10, marginBottom: 12,
              background: remaining.expired ? "rgba(220,38,38,0.10)" : "rgba(234,179,8,0.12)",
              border: `1px solid ${remaining.expired ? "#dc2626" : "#eab308"}`,
              color: remaining.expired ? "#dc2626" : "#a16207",
              fontSize: 12, fontWeight: 800, display: "flex", alignItems: "center", gap: 6,
            }} data-testid="appointment-deadline">
              <CalendarCheck size={14} />
              {remaining.expired
                ? t("انتهت مهلة الاختيار — سيُلغى الطلب تلقائياً.",
                    "Selection window ended — request will be auto-cancelled.",
                    "انتخاب کا وقت ختم ہو گیا — درخواست خودبخود منسوخ ہو جائے گی۔")
                : t(`تبقى ${remaining.h} ساعة و ${remaining.m} دقيقة لاختيار موعدك (إلغاء تلقائي بعدها).`,
                    `${remaining.h}h ${remaining.m}m left to pick (auto-cancel after).`,
                    `${remaining.h} گھنٹے ${remaining.m} منٹ باقی ہیں۔`)}
            </div>
          )}
          {state.options.map((o) => {
            const dtLabel = new Date(o.datetime).toLocaleString("en-GB", { timeZone: "Asia/Riyadh", hour12: false });
            return (
            <button key={o.id} onClick={() => pick(o.id, dtLabel)} disabled={busy}
              style={{
                display: "block", width: "100%", textAlign: "start", marginBottom: 8,
                padding: "12px 14px", borderRadius: 10, border: `1.5px solid ${theme.borderAccent}`,
                background: theme.surface, color: theme.text, cursor: "pointer",
                opacity: busy ? 0.5 : 1, fontFamily: "inherit",
              }}
              data-testid={`appointment-option-${o.id}`}>
              <div style={{ fontWeight: 800, fontSize: 14 }} dir="ltr">
                {dtLabel}
              </div>
            </button>
            );
          })}
          {err && <div style={{ color: "#dc2626", fontSize: 11, marginTop: 6 }}>{err}</div>}
        </>
      )}
    </section>
  );
}

/* ───────────────── Muaqqib (legal rep) info ───────────────── */
function MuaqqibInfoBlock({ applicationNumber, theme, lang, dir }) {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    if (!applicationNumber) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/portal/track/${applicationNumber}/muaqqib-profile`)
      .then(({ data }) => setProfile(data))
      .catch(() => setProfile(null));
  }, [applicationNumber]);
  if (!profile) return null;
  const t = (ar, en, ur) => lang === "ur" ? ur : lang === "en" ? en : ar;
  const imgUrl = `${process.env.REACT_APP_BACKEND_URL}/api/portal/track/${applicationNumber}/muaqqib-profile/id-image`;
  // Build a tel: link that strips spaces / dashes so any device dialer accepts it
  const cleanPhone = (profile.phone || "").replace(/[^+\d]/g, "");
  return (
    <section dir={dir} style={{
      background: "rgba(212,175,55,0.06)", border: `2px dashed ${theme.borderAccent}`,
      borderRadius: 16, padding: 18, marginBottom: 18,
    }} data-testid="muaqqib-info-block">
      <h3 style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 900, color: theme.gold }}>
        {t("بيانات المعقّب — لتفعيل الوكالة في ناجز", "Your Legal Representative (Najiz)", "آپ کا قانونی نمائندہ (Najiz)")}
      </h3>
      <p style={{ fontSize: 11, lineHeight: 1.7, opacity: 0.85, marginBottom: 10 }}>
        {t("لإصدار توكيل المعقّب على بوابة ناجز، استخدم بياناته أدناه. تواصل معه مباشرة لأي استفسار.",
           "Use the details below to issue the Najiz power of attorney. Contact him directly for any questions.",
           "Najiz پر وکالت جاری کرنے کے لیے درج ذیل تفصیلات استعمال کریں۔")}
      </p>
      <div style={{ background: theme.surfaceSubtle, borderRadius: 10, padding: 12, fontSize: 13, lineHeight: 2 }}>
        <MqRow label={t("الاسم", "Name", "نام")} value={profile.full_name} />
        <MqRow label={t("رقم الهوية", "National ID", "قومی شناختی نمبر")} value={profile.national_id} ltr />
        {profile.date_of_birth && <MqRow label={t("تاريخ الميلاد", "Date of Birth", "تاریخ پیدائش")} value={profile.date_of_birth} ltr />}
        <MqRow label={t("الجوال", "Mobile", "موبائل")} value={profile.phone} ltr />
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
        {cleanPhone && (
          <a href={`tel:${cleanPhone}`} style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 14px", background: "#16a34a", color: "#fff",
            borderRadius: 8, fontWeight: 800, fontSize: 12, textDecoration: "none",
          }} data-testid="muaqqib-call">
            📞 {t("اتصل بالمعقّب", "Call", "کال کریں")}
          </a>
        )}
        {cleanPhone && (
          <a href={`https://wa.me/${cleanPhone.replace(/^\+/, "")}`} target="_blank" rel="noopener noreferrer" style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 14px", background: "#25D366", color: "#fff",
            borderRadius: 8, fontWeight: 800, fontSize: 12, textDecoration: "none",
          }} data-testid="muaqqib-whatsapp">
            💬 {t("واتساب", "WhatsApp", "WhatsApp")}
          </a>
        )}
        {profile.id_image_present && (
          <a href={imgUrl} target="_blank" rel="noopener noreferrer" style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 14px", background: theme.gold, color: "#0a0a0a",
            borderRadius: 8, fontWeight: 800, fontSize: 12, textDecoration: "none",
          }} data-testid="muaqqib-download-id">
            <ExternalLink size={14} strokeWidth={2.4} />
            {t("عرض هوية المعقّب", "View Muaqqib's ID", "Muaqqib کی شناخت دیکھیں")}
          </a>
        )}
      </div>
    </section>
  );
}

function MqRow({ label, value, ltr }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
      <span style={{ opacity: 0.7, fontSize: 12 }}>{label}</span>
      <span style={{ fontWeight: 800, direction: ltr ? "ltr" : "rtl" }}>{value || "—"}</span>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Office Document Requests — popup + list
// ──────────────────────────────────────────────────────────────

function DocsRequestPopup({ count, theme, lang, dir, onOpenList, onLater }) {
  const titles = {
    ar: "المكتب يطلب منك مستندات",
    en: "Office requested documents",
    ur: "دفتر نے دستاویزات طلب کی ہیں",
  };
  const subs = {
    ar: `${count} مستند${count > 2 ? "اً" : ""} مطلوب${count > 1 ? "ة" : ""} لإكمال معاملتك`,
    en: `${count} document${count > 1 ? "s" : ""} required to complete your application`,
    ur: `آپ کی درخواست مکمل کرنے کے لیے ${count} دستاویزات درکار ہیں`,
  };
  const btnOpen = { ar: "افتح القائمة", en: "Open list", ur: "فہرست کھولیں" };
  const btnLater = { ar: "لاحقاً", en: "Later", ur: "بعد میں" };
  return (
    <div
      dir={dir}
      data-testid="doc-request-popup"
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, animation: "vta-fade-in 240ms ease",
      }}
    >
      <div style={{
        width: "100%", maxWidth: 380,
        background: theme.surfaceCard, color: theme.text,
        border: `1.5px solid ${theme.gold}`,
        borderRadius: 20, padding: "22px 20px 20px",
        boxShadow: "0 20px 60px rgba(212,175,55,0.18), 0 0 0 4px rgba(212,175,55,0.06)",
        animation: "vta-pop 320ms cubic-bezier(.2,.9,.4,1.2)",
      }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 10, color: theme.gold }}>
          <Paperclip size={28} strokeWidth={2.2} />
        </div>
        <div style={{ fontSize: 17, fontWeight: 900, color: theme.gold, textAlign: "center", marginBottom: 4 }}>
          {titles[lang] || titles.ar}
        </div>
        <div style={{ fontSize: 12, color: theme.textMuted || "#888", textAlign: "center", marginBottom: 18, lineHeight: 1.6 }}>
          {subs[lang] || subs.ar}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={onOpenList}
            data-testid="doc-popup-open"
            style={{
              flex: 1, padding: "13px", borderRadius: 12, border: "none",
              background: "linear-gradient(135deg, #f4d03f 0%, #b8902a 100%)",
              color: "#1a1a1a", fontSize: 14, fontWeight: 900, cursor: "pointer", fontFamily: "inherit",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
            <Upload size={16} strokeWidth={2.4} />
            <span>{btnOpen[lang] || btnOpen.ar}</span>
          </button>
          <button
            onClick={onLater}
            data-testid="doc-popup-later"
            style={{
              padding: "13px 16px", borderRadius: 12,
              background: "transparent", color: theme.textSoft || theme.text,
              border: `1px solid ${theme.border}`,
              fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            }}>
            {btnLater[lang] || btnLater.ar}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes vta-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes vta-pop { from { transform: scale(.88); opacity: 0 } to { transform: scale(1); opacity: 1 } }
      `}</style>
    </div>
  );
}


function DocsRequestList({ docRequests, applicationNumber, mobileNumber, theme, lang, dir, onUploaded }) {
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");

  const title = { ar: "المستندات التي طلبها المكتب", en: "Documents requested by the office", ur: "دفتر کی طلب کردہ دستاویزات" }[lang] || "المستندات التي طلبها المكتب";
  const upload = { ar: "ارفع", en: "Upload", ur: "اپ لوڈ" }[lang] || "ارفع";
  const done = { ar: "تم", en: "Uploaded", ur: "مکمل" }[lang] || "تم";
  const pending = { ar: "مطلوب", en: "Required", ur: "درکار" }[lang] || "مطلوب";

  const handleUpload = async (req, file) => {
    setBusyId(req.id); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (req.kind === "note") {
        // Legacy stage-note doc: use the older `/reupload` endpoint that
        // expects a `note_id` form field.
        fd.append("note_id", req.id);
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/portal/track/${applicationNumber}/reupload`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        fd.append("mobile_number", mobileNumber || "");
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/portal/track/${applicationNumber}/doc-requests/${req.id}/upload`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }
      if (onUploaded) onUploaded();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed");
    } finally { setBusyId(null); }
  };

  return (
    <section
      dir={dir}
      data-testid="doc-request-list"
      style={{
        background: theme.surfaceCard,
        border: `1px solid ${theme.borderSoft || theme.border}`,
        borderRadius: 16, padding: 14, marginBottom: 14,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 800, color: theme.gold, marginBottom: 12, display: "inline-flex", alignItems: "center", gap: 8 }}>
        <Paperclip size={14} strokeWidth={2.4} />
        <span>{title}</span>
      </div>
      {err && <div style={{ color: "#dc2626", fontSize: 12, marginBottom: 8 }}>{err}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {docRequests.map((r, idx) => {
          const isDone = r.status === "uploaded";
          return (
            <label
              key={r.id}
              data-testid={`doc-request-row-${idx + 1}`}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 14px",
                background: isDone ? "rgba(22,163,74,0.06)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${isDone ? "rgba(22,163,74,0.4)" : theme.borderSoft || theme.border}`,
                borderRadius: 12,
                cursor: isDone ? "default" : "pointer",
                transition: "border-color 160ms",
              }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: isDone ? "#16a34a" : theme.gold,
                color: isDone ? "#fff" : "#1a1a1a",
                fontWeight: 900, fontSize: 13,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>{idx + 1}</div>
              <div style={{ flex: 1, fontSize: 14, fontWeight: 800, color: theme.text }}>
                {r.title}
              </div>
              {isDone ? (
                <span style={{
                  fontSize: 11, fontWeight: 800,
                  padding: "4px 10px", borderRadius: 999,
                  background: "rgba(22,163,74,0.16)", color: "#16a34a",
                  border: "1px solid rgba(22,163,74,0.4)",
                  display: "inline-flex", alignItems: "center", gap: 4,
                }}><Check size={11} strokeWidth={3} /><span>{done}</span></span>
              ) : (
                <>
                  <span style={{
                    fontSize: 11, fontWeight: 800,
                    padding: "4px 10px", borderRadius: 999,
                    background: "rgba(220,38,38,0.14)", color: "#dc2626",
                    border: "1px solid rgba(220,38,38,0.4)",
                  }}>{pending}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 800,
                    padding: "5px 10px", borderRadius: 8,
                    background: theme.gold, color: "#1a1a1a",
                    opacity: busyId === r.id ? 0.6 : 1,
                  }}>{busyId === r.id ? "..." : upload}</span>
                  <input
                    type="file"
                    style={{ display: "none" }}
                    onChange={(e) => { if (e.target.files?.[0]) handleUpload(r, e.target.files[0]); }}
                    data-testid={`doc-request-upload-${idx + 1}`}
                  />
                </>
              )}
            </label>
          );
        })}
      </div>
    </section>
  );
}
